import { useEffect, useCallback, useRef, useState } from "react";
import { usePricingCalculation } from "./usePricingCalculation";
import { useOrder } from "../contexts";
import { useOrderQuery } from "./useOrder";
import { PricingCalculationConfig } from "../types/pricing";
import { getCurrencyBasedOnDomain } from "../utils/domain.util";
import { debugLog } from "src/utils/debug-log";

const EMPTY_PHOTO_GROUPS: never[] = [];
const EMPTY_QUANTITIES = {};

/**
 * Hook that automatically updates pricing when photo groups or product quantities change
 * Integrates with existing contexts to provide real-time server-calculated pricing
 * Follows Azure best practices for error handling and service reliability
 *
 * NOTE: this hook is currently vestigial. `photoGroups` and `quantities` are
 * permanently empty (they used to come from ProductContext, which was removed),
 * so `updateAllPricing` always hits its "no items" early-return and no pricing
 * request is ever actually made from here. It is left in place because callers
 * read `isCalculating` / `error` off it, but nothing below the early-return runs.
 */
export const usePricingIntegration = (
  config: PricingCalculationConfig = {}
) => {
  const {
    calculatePricing,
    transformPhotoGroupsToApiFormat,
    transformProductsToApiFormat,

    error,
  } = usePricingCalculation();

  // Module-level constants, NOT fresh literals per render. As inline `[]` / `{}`
  // they got a new identity on every render, so the effects below (which list
  // photoGroups in their dependency array) re-ran after every single render of
  // every component using this hook. They happened not to loop only because the
  // JSON.stringify guard short-circuits; the churn was still real.
  const photoGroups = EMPTY_PHOTO_GROUPS;
  // ProductContext has been removed - quantities are no longer tracked locally
  const quantities = EMPTY_QUANTITIES; // Empty since products are managed via API
  const { mainOrderId, guestToken } = useOrder();
  const { data: order } = useOrderQuery(mainOrderId, guestToken);
  const [isCalculating, setIsCalculating] = useState(false); // Placeholder, can be replaced with actual state if needed
  // Use refs to track if we need to trigger pricing updates
  const photoGroupsRef = useRef(photoGroups);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  /**
   * Main function to calculate and update all pricing
   * Debounced to avoid excessive API calls during rapid changes
   */
  const updateAllPricing = useCallback(async () => {
    try {
      setIsCalculating(false);
      // Transform data to API format
      const photoSizeItems = transformPhotoGroupsToApiFormat(photoGroups);
      const productItems = transformProductsToApiFormat(quantities);

      // Skip calculation if no items
      if (photoSizeItems.length === 0 && productItems.length === 0) {
        return;
      }

      // Get configuration from order or use defaults
      const pricingConfig: PricingCalculationConfig = {
        currency: config.currency || getCurrencyBasedOnDomain(),
        shippingMethod:
          config.shippingMethod ||
          (order?.shippingMethod as PricingCalculationConfig["shippingMethod"]) ||
          "normal",
        paymentOption:
          config.paymentOption ||
          (order?.paymentOption as PricingCalculationConfig["paymentOption"]) ||
          "NORMAL",
        couponId:
          config.couponId ||
          (order?.couponUsed as { id?: string } | undefined)?.id ||
          undefined,
        deliveryType:
          config.deliveryType ||
          (order?.deliveryType as PricingCalculationConfig["deliveryType"]) ||
          "HOME",
      };

      debugLog("Updating pricing with config:", pricingConfig);

      // Calculate pricing via API
      const pricingResult = await calculatePricing(
        photoSizeItems,
        productItems,
        pricingConfig
      );
      debugLog("Pricing calculation result:", pricingResult);
      if (pricingResult) {
        // Note: Local photo group prices are no longer cached
        // Pricing is calculated server-side and stored in the order
        debugLog("Pricing updated successfully:", pricingResult);
        setIsCalculating(false);
        return pricingResult;
      }
    } catch (err) {
      console.error("Failed to update pricing:", err);
      setIsCalculating(false);
    }
  }, [order]);

  /**
   * Debounced pricing update function
   * Delays API calls to avoid excessive requests during rapid changes
   */
  const debouncedUpdatePricing = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      updateAllPricing();
    }, 500); // 500ms delay
  }, [updateAllPricing]);

  /**
   * Effect to trigger pricing updates when photo groups change
   */
  useEffect(() => {
    const hasPhotoGroupsChanged =
      JSON.stringify(photoGroups) !== JSON.stringify(photoGroupsRef.current);

    if (hasPhotoGroupsChanged) {
      photoGroupsRef.current = photoGroups;
      debouncedUpdatePricing();
    }
  }, [photoGroups, debouncedUpdatePricing]);

  /**
   * Effect to trigger pricing updates when product quantities change
   * ProductContext has been removed - product quantities are now managed via API
   */
  // useEffect(() => {
  //   const hasQuantitiesChanged =
  //     JSON.stringify(quantities) !== JSON.stringify(quantitiesRef.current);

  //   if (hasQuantitiesChanged) {
  //     quantitiesRef.current = quantities;
  //     debouncedUpdatePricing();
  //   }
  // }, [quantities, debouncedUpdatePricing]);

  /**
   * Effect to trigger pricing updates when order configuration changes
   * (shipping method, payment option, coupon, etc.)
   */
  useEffect(() => {
    if (order) {
      debouncedUpdatePricing();
    }
  }, [
    order?.shippingMethod,
    order?.paymentOption,
    (order?.couponUsed as { id?: string } | undefined)?.id,
    debouncedUpdatePricing,
  ]);

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Manual trigger for pricing updates
   */
  const triggerPricingUpdate = useCallback(() => {
    updateAllPricing();
  }, [updateAllPricing]);

  return {
    triggerPricingUpdate,
    isCalculating,
    error,
  };
};
